import React, { useCallback, useState, useEffect } from 'react'
import { useLogin } from '../hooks'
import { useOrderFilters } from '../lib/hooks/useOrderFilters'
import { Button } from './shadcn/Button'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faBuilding,
  faCalendar,
  faCashRegister,
  faChevronDown,
  faGlobe,
  faList,
  faSearch,
  faSort,
  faTag,
  faTruck,
  faWarehouse,
  faXmark,
} from '@fortawesome/pro-solid-svg-icons'
import { useQueryClient } from '@tanstack/react-query'
import { Input } from './shadcn/Input'
import { Combox } from './shadcn/combox'
import { Popover, PopoverContent, PopoverTrigger } from './shadcn/popover'
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from './shadcn/command'
import { CheckIcon, ChevronsUpDownIcon } from 'lucide-react'
import { Tag } from './Tag'
import { useBasicEntities } from '../hooks/useBasicEntities'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './shadcn/collapsible'
import { DatePicker } from './shadcn/datepicker'
import { CarrierSelector } from './CarrierSelector'

export function OrdersFilters(props: { onFilter?: () => void }) {
  const { onFilter } = props
  const { depots, checkouts, tags, countries } = useBasicEntities()
  const { user } = useLogin()
  const childCompanies = user.company.children
  const queryClient = useQueryClient()

  // Use the hook for filter management
  const {
    searchType,
    setSearchType,
    localSearchString,
    setSearchString,
    setLocalSearchString,
    selectedDepotId,
    setSelectedDepotId,
    selectedCheckoutId,
    setSelectedCheckoutId,
    selectedCarrierIds,
    setSelectedCarrierIds,
    selectedCountry,
    setSelectedCountry,
    selectedTagIds,
    setSelectedTagIds,
    selectedDate,
    setSelectedDate,
    selectedChildCompanyId,
    setSelectedChildCompany,
    resetFilters,
    sort,
    direction,
    setSort,
    processStatus,
  } = useOrderFilters()

  const [isFiltersOpen, setIsFiltersOpen] = useState(processStatus === 'dispatched')
  useEffect(() => {
    if (processStatus === 'dispatched') {
      setIsFiltersOpen(true)
    }
  }, [processStatus])

  const refetch = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['orders'], stale: true })
    onFilter?.()
  }, [queryClient, onFilter])

  const searchTypes = [
    { value: 'reservationNumber', label: 'Číslo rezervace' },
    { value: 'billNumber', label: 'Číslo objednávky' },
    { value: 'package', label: 'Balík' },
    { value: 'box', label: 'Přepravka' },
    { value: 'customer', label: 'Zákazník' },
    { value: 'fulltext', label: 'Fulltext' },
  ]

  // Transform depots, checkouts, etc. into format expected by FormSelect
  const depotOptions = [
    { value: 'all', label: 'Vše' },
    ...depots.map(depot => ({ value: String(depot.id), label: depot.name })),
  ]

  const checkoutOptions = [
    { value: 'all', label: 'Vše' },
    ...checkouts.map(checkout => ({ value: String(checkout.id), label: checkout.name })),
  ]

  const countryOptions = [
    { value: 'all', label: 'Vše' },
    ...countries.map(country => ({ value: country.code, label: country.name })),
  ]

  const filteredTags = tags.filter(tag => tag.type === 'order')
  const tagOptions = filteredTags.map(tag => ({ value: String(tag.id), label: tag.name }))

  const childCompanyOptions = [
    { value: 'all', label: 'Vše' },
    ...childCompanies.map(childCompany => ({ value: childCompany.id.toString(), label: childCompany.name })),
  ]

  return (
    <div className="orders-filters bg-white p-4 shadow">
      <div className="flex flex-col gap-2">
        {/* First row */}
        <Collapsible className="space-y-2" open={isFiltersOpen} onOpenChange={setIsFiltersOpen}>
          <div className="grid grid-cols-6 gap-2">
            <div className="flex col-span-2 gap-2">
              <div className="form-control gap-1 grow-[1]">
                <label className="flex items-center gap-2">
                  <FontAwesomeIcon icon={faList} /> Typ vyhledávání
                </label>
                <Combox
                  className="flex-1"
                  items={searchTypes.map(option => ({ value: String(option.value), label: option.label }))}
                  placeholder="Vyberte typ vyhledávání"
                  value={searchType ? String(searchType) : undefined}
                  onValueChange={value => setSearchType(value)}
                  disableSearch
                />
              </div>

              <div className={`form-control gap-1 grow-[5]`}>
                <label className="flex items-center gap-2">
                  <FontAwesomeIcon icon={faSearch} /> Hledat
                </label>
                <div className="flex">
                  <Input
                    type="text"
                    className="flex-1"
                    value={localSearchString}
                    onChange={e => setLocalSearchString(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        setSearchString(localSearchString)
                      }
                    }}
                    placeholder="Zadejte hledaný výraz"
                  />
                </div>
              </div>
            </div>

            {childCompanies.length > 0 && (
              <div className="form-control gap-1">
                <label className="flex items-center gap-2">
                  <FontAwesomeIcon icon={faBuilding} /> Firma
                </label>
                <Combox
                  items={childCompanyOptions}
                  placeholder="Vyberte firmu"
                  value={selectedChildCompanyId ? String(selectedChildCompanyId) : 'all'}
                  onValueChange={value => setSelectedChildCompany(value === 'all' ? undefined : value)}
                />
              </div>
            )}

            <div className="form-control gap-1">
              <label className="flex items-center gap-2">
                <FontAwesomeIcon icon={faTruck} /> Dopravce
              </label>
              <CarrierSelector mode="multi" value={selectedCarrierIds} onValueChange={setSelectedCarrierIds} />
            </div>

            <div className="form-control gap-1">
              <label className="flex items-center gap-2">
                <FontAwesomeIcon icon={faCashRegister} className="mb-[2px]" /> Pokladna
              </label>
              <Combox
                items={checkoutOptions}
                placeholder="Vyberte pokladnu"
                value={selectedCheckoutId ? String(selectedCheckoutId) : 'all'}
                onValueChange={value => setSelectedCheckoutId(value === 'all' ? undefined : value)}
              />
            </div>

            {childCompanies.length == 0 && (
              <div className="form-control gap-1">
                <label className="flex items-center gap-2">
                  <FontAwesomeIcon icon={faTag} /> Tagy
                </label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      className="justify-between text-left bg-gray-100"
                      size="lg">
                      <span className="truncate">
                        {selectedTagIds.length === 0
                          ? 'Vyberte tagy'
                          : selectedTagIds.length === 1
                            ? tagOptions.find(tag => tag.value === String(selectedTagIds[0]))?.label
                            : selectedTagIds.length === 2
                              ? tagOptions
                                  .filter(tag => selectedTagIds.includes(Number(tag.value)))
                                  .map(tag => tag.label)
                                  .join(', ')
                              : `${selectedTagIds.length} položek vybráno`}
                      </span>
                      <ChevronsUpDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="p-0">
                    <Command>
                      <CommandInput placeholder="Vyberte tagy" />
                      <CommandList>
                        <CommandEmpty>Nenalezeny žádné položky.</CommandEmpty>
                        <CommandGroup>
                          {tagOptions.map(tag => {
                            const tagId = Number(tag.value)
                            const isSelected = selectedTagIds.includes(tagId)
                            const tagData = tags.find(t => t.id === tagId)
                            return (
                              <CommandItem
                                key={tag.value}
                                onSelect={() => {
                                  const updatedTagIds = isSelected
                                    ? selectedTagIds.filter(id => id !== tagId)
                                    : [...selectedTagIds, tagId]
                                  setSelectedTagIds(updatedTagIds)
                                }}
                                className="cursor-pointer">
                                {tagData && <Tag tag={tagData} />}
                                <CheckIcon className={`mr-2 h-4 w-4 ${isSelected ? 'opacity-100' : 'opacity-0'}`} />
                              </CommandItem>
                            )
                          })}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
            )}

            <div className="flex items-end gap-2 col-span-1">
              <Button
                onClick={() => {
                  resetFilters()
                  setLocalSearchString('')
                }}
                variant="outlineDanger"
                size="lg">
                <FontAwesomeIcon icon={faXmark} />
              </Button>
              <Button onClick={refetch} variant="default" className="flex-1" size="lg">
                <FontAwesomeIcon icon={faSearch} />
                Vyhledat
              </Button>
              <CollapsibleTrigger asChild>
                <Button variant="outline" size="lg" className="w-24">
                  <FontAwesomeIcon icon={faChevronDown} className="rotate" />
                </Button>
              </CollapsibleTrigger>
            </div>
          </div>

          {/* Second row */}
          <CollapsibleContent>
            <div className="grid grid-cols-6 gap-2">
              {childCompanies.length > 0 && (
                <div className="form-control gap-1">
                  <label className="flex items-center gap-2">
                    <FontAwesomeIcon icon={faTag} /> Tagy
                  </label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        className="justify-between text-left bg-gray-100"
                        size="lg">
                        <span className="truncate">
                          {selectedTagIds.length === 0
                            ? 'Vyberte tagy'
                            : selectedTagIds.length === 1
                              ? tagOptions.find(tag => tag.value === String(selectedTagIds[0]))?.label
                              : selectedTagIds.length === 2
                                ? tagOptions
                                    .filter(tag => selectedTagIds.includes(Number(tag.value)))
                                    .map(tag => tag.label)
                                    .join(', ')
                                : `${selectedTagIds.length} položek vybráno`}
                        </span>
                        <ChevronsUpDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="p-0">
                      <Command>
                        <CommandInput placeholder="Vyberte tagy" />
                        <CommandList>
                          <CommandEmpty>Nenalezeny žádné položky.</CommandEmpty>
                          <CommandGroup>
                            {tagOptions.map(tag => {
                              const tagId = Number(tag.value)
                              const isSelected = selectedTagIds.includes(tagId)
                              const tagData = tags.find(t => t.id === tagId)
                              return (
                                <CommandItem
                                  key={tag.value}
                                  onSelect={() => {
                                    const updatedTagIds = isSelected
                                      ? selectedTagIds.filter(id => id !== tagId)
                                      : [...selectedTagIds, tagId]
                                    setSelectedTagIds(updatedTagIds)
                                  }}
                                  className="cursor-pointer">
                                  {tagData && <Tag tag={tagData} />}
                                  <CheckIcon className={`mr-2 h-4 w-4 ${isSelected ? 'opacity-100' : 'opacity-0'}`} />
                                </CommandItem>
                              )
                            })}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>
              )}
              <div className="form-control gap-1">
                <label className="flex items-center gap-2">
                  <FontAwesomeIcon icon={faGlobe} /> Země
                </label>
                <Combox
                  items={countryOptions}
                  placeholder="Vyberte zemi"
                  value={selectedCountry ? String(selectedCountry) : 'all'}
                  onValueChange={value => setSelectedCountry(value === 'all' ? undefined : value)}
                />
              </div>

              <div className="form-control gap-1">
                <label className="flex items-center gap-2">
                  <FontAwesomeIcon icon={faWarehouse} className="mb-[2px]" /> Sklad
                </label>
                <Combox
                  items={depotOptions}
                  placeholder="Vyberte sklad"
                  value={selectedDepotId ? String(selectedDepotId) : 'all'}
                  onValueChange={value => setSelectedDepotId(value === 'all' ? undefined : value)}
                />
              </div>
              <div className="form-control gap-1">
                <label className="flex items-center gap-2">
                  {' '}
                  <FontAwesomeIcon icon={faCalendar} />
                  Datum změny stavu
                </label>
                <DatePicker
                  date={selectedDate === 'all' ? undefined : selectedDate ? new Date(selectedDate) : undefined}
                  setDate={d => {
                    if (!d) {
                      setSelectedDate(undefined)
                      return
                    }
                    const dateStr = `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, '0')}-${d.getDate().toString().padStart(2, '0')}`
                    const existingHour = selectedDate?.match(/T(\d{2}):/)?.[1]
                    setSelectedDate(existingHour ? `${dateStr}T${existingHour}:00` : dateStr)
                  }}
                  hour={selectedDate?.match(/T(\d{2}):/)?.[1]}
                  onHourChange={
                    processStatus === 'picking' || processStatus === 'packing'
                      ? hour => {
                          const datePart = selectedDate?.split('T')[0]
                          if (!datePart) return
                          setSelectedDate(hour ? `${datePart}T${hour}:00` : datePart)
                        }
                      : undefined
                  }
                />
              </div>
              <div className="form-control gap-1 col-span-2">
                <label className="flex items-center gap-2">
                  <FontAwesomeIcon icon={faSort} /> Řazení
                </label>
                <Combox
                  items={[
                    { value: 'dateExpedition_asc', label: 'Datum expedice od nejstaršího' },
                    { value: 'dateExpedition_desc', label: 'Datum expedice od nejnovějšího' },
                    { value: 'processStatusUpdated_asc', label: 'Datum změny stavu od nejstaršího' },
                    { value: 'processStatusUpdated_desc', label: 'Datum změny stavu od nejnovějšího' },
                    { value: 'dateCreated_asc', label: 'Datum vytvoření od nejstaršího' },
                    { value: 'dateCreated_desc', label: 'Datum vytvoření od nejnovějšího' },
                    { value: 'priority_asc', label: 'Priorita od nejnižší' },
                    { value: 'priority_desc', label: 'Priorita od nejvyšší' },
                  ]}
                  placeholder="Vyberte řazení"
                  value={sort && direction ? `${sort}_${direction.toLowerCase()}` : ``}
                  onValueChange={value => {
                    if (value) {
                      const [field, dir] = value.split('_')
                      setSort(field, dir.toUpperCase() as 'ASC' | 'DESC')
                    }
                  }}
                />
              </div>
            </div>
          </CollapsibleContent>
        </Collapsible>
      </div>
    </div>
  )
}
